// src/components/SEOFixesDashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
  Page, 
  Card, 
  Tabs, 
  Badge, 
  Button, 
  Layout, 
  Text, 
  Thumbnail,
  DataTable,
  Banner,
  Toast,
  Frame,
  Loading
} from '@shopify/polaris';
import { useQuery, useMutation } from '@apollo/client';
import { 
  GET_SEO_ISSUES, 
  APPLY_FIX, 
  REGENERATE_SUGGESTION,
  BULK_APPLY_FIXES
} from '../../graphql/seoQueries';
import { useAppBridge } from '@shopify/app-bridge-react';

export default function SEOFixesDashboard(){
  // const app = useAppBridge();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [undoAction, setUndoAction] = useState(null);

  // GraphQL Queries
  const { loading, error, data, refetch } = useQuery(GET_SEO_ISSUES, {
    variables: { shopId: 'current' },
    fetchPolicy: 'network-only'
  });

  // GraphQL Mutations
  const [applyFix] = useMutation(APPLY_FIX);
  const [regenerateSuggestion] = useMutation(REGENERATE_SUGGESTION);
  const [bulkApplyFixes] = useMutation(BULK_APPLY_FIXES);

  // Handle tab change
  const handleTabChange = (selectedTabIndex) => {
    setActiveTab(selectedTabIndex);
    setSelectedItems([]);
  };

  // Apply fix to a single item
  const handleApplyFix = async (type, id) => {
    try {
      const { data } = await applyFix({
        variables: { input: { issueType: type, issueId: id } }
      });
      
      setUndoAction({
        type: 'single',
        issueType: type,
        issueId: id,
        previousState: data.applyFix.previousState
      });
      
      setToastContent('Fix applied successfully');
      setShowToast(true);
      refetch();
    } catch (err) {
      console.error('Error applying fix:', err);
    }
  };

  // Regenerate suggestion
  const handleRegenerateSuggestion = async (type, id) => {
    try {
      await regenerateSuggestion({
        variables: { input: { issueType: type, issueId: id } }
      });
      refetch();
    } catch (err) {
      console.error('Error regenerating suggestion:', err);
    }
  };

  // Apply bulk action
  const handleBulkApply = async () => {
    if (!bulkAction || selectedItems.length === 0) return;
    
    try {
      const { data } = await bulkApplyFixes({
        variables: { 
          input: { 
            issueType: getCurrentIssueType(), 
            issueIds: selectedItems,
            action: bulkAction
          } 
        }
      });
      
      setUndoAction({
        type: 'bulk',
        issueType: getCurrentIssueType(),
        issueIds: selectedItems,
        action: bulkAction,
        previousStates: data.bulkApplyFixes.previousStates
      });
      
      setToastContent(`Applied ${bulkAction} to ${selectedItems.length} items`);
      setShowToast(true);
      setSelectedItems([]);
      setBulkAction('');
      refetch();
    } catch (err) {
      console.error('Error applying bulk action:', err);
    }
  };

  // Undo last action
  const handleUndo = async () => {
    if (!undoAction) return;
    
    try {
      // In a real app, you would send a mutation to undo the action
      // This is just a simulation
      console.log('Undoing action:', undoAction);
      setToastContent('Undo successful');
      setShowToast(true);
      setUndoAction(null);
      refetch();
    } catch (err) {
      console.error('Error undoing action:', err);
    }
  };

  // Get current issue type based on active tab
  const getCurrentIssueType = () => {
    switch (activeTab) {
      case 0: return 'META_TAGS';
      case 1: return 'ALT_TEXT';
      case 2: return 'BROKEN_LINKS';
      case 3: return 'CONTENT';
      default: return 'META_TAGS';
    }
  };

  // Toggle item selection
  const toggleItemSelection = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  if (loading) return (
    <Frame>
      <Loading />
    </Frame>
  );

  if (error) return (
    <Banner status="critical">
      Error loading SEO issues: {error.message}
    </Banner>
  );

  const { seoIssues } = data;
  const { metaTags, altText, brokenLinks, content } = seoIssues;

  return (
    <Page
      title="SEO Audit & Auto-Fixes"
      subtitle="Automatically fix SEO issues to improve your store's visibility"
      primaryAction={{
        content: 'Run New Scan',
        onAction: () => refetch(),
      }}
    >
      {/* Progress Summary Card */}
      <Card sectioned>
        <Layout>
          <Layout.Section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text variant="headingMd" as="h6" fontWeight="bold">Fixable Issues</Text>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <Badge status="critical">{seoIssues.criticalCount} Critical</Badge>
                  <Badge status="warning">{seoIssues.warningCount} Warnings</Badge>
                  <Text as="span" color="subdued">
                    {seoIssues.fixableCount} of {seoIssues.totalCount} issues can be fixed automatically
                  </Text>
                </div>
              </div>
              <div>
                <Text variant="headingMd" as="h6" fontWeight="bold">SEO Health Impact</Text>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                  <Text variant="headingMd" as="h6" fontWeight="bold" tone="critical">{seoIssues.currentScore}</Text>
                  <span style={{ margin: '0 8px' }}>→</span>
                  <Text variant="headingMd" as="h6" fontWeight="bold" tone="success">{seoIssues.potentialScore}</Text>
                  <Button primary style={{ marginLeft: '16px' }} onClick={() => handleBulkApply()}>
                    Fix All Now
                  </Button>
                </div>
              </div>
            </div>
          </Layout.Section>
        </Layout>
      </Card>

      {/* Main Tabs */}
      <Card>
        <Tabs tabs={[
          {
            id: 'meta-tags',
            content: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Meta Tags</span>
                <Badge status={metaTags.length > 0 ? 'attention' : undefined}>
                  {metaTags.length}
                </Badge>
              </div>
            ),
            accessibilityLabel: 'Meta Tags',
            panelID: 'meta-tags-panel',
          },
          {
            id: 'alt-text',
            content: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Alt Text</span>
                <Badge status={altText.length > 0 ? 'attention' : undefined}>
                  {altText.length}
                </Badge>
              </div>
            ),
            accessibilityLabel: 'Alt Text',
            panelID: 'alt-text-panel',
          },
          {
            id: 'broken-links',
            content: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Broken Links</span>
                <Badge status={brokenLinks.length > 0 ? 'attention' : undefined}>
                  {brokenLinks.length}
                </Badge>
              </div>
            ),
            accessibilityLabel: 'Broken Links',
            panelID: 'broken-links-panel',
          },
          {
            id: 'content',
            content: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Content</span>
                <Badge status={content.length > 0 ? 'attention' : undefined}>
                  {content.length}
                </Badge>
              </div>
            ),
            accessibilityLabel: 'Content',
            panelID: 'content-panel',
          },
        ]}
        selected={activeTab}
        onSelect={handleTabChange}
        >
          {/* Meta Tags Panel */}
          {activeTab === 0 && (
            <Card.Section>
              {metaTags.length === 0 ? (
                <Banner status="success">
                  No meta tag issues found. Your store's meta tags are optimized!
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['', 'Page', 'Current', 'Suggested']}
                  rows={metaTags.map((item) => [
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                      key={`checkbox-${item.id}`}
                    />,
                    <Text variant="headingMd" as="h6" fontWeight="bold">
                      <a href={item.pageUrl} target="_blank" rel="noopener noreferrer">
                        {item.pageUrl}
                      </a>
                    </Text>,
                    item.currentTitle || <Text as="span" color="critical">Missing</Text>,
                    item.suggestedTitle,
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button size="slim" onClick={() => handleApplyFix('META_TAGS', item.id)}>
                        Apply
                      </Button>
                      <Button size="slim" onClick={() => handleRegenerateSuggestion('META_TAGS', item.id)}>
                        Regenerate
                      </Button>
                    </div>
                  ])}
                />
              )}
            </Card.Section>
          )}

          {/* Alt Text Panel */}
          {activeTab === 1 && (
            <Card.Section>
              {altText.length === 0 ? (
                <Banner status="success">
                  No alt text issues found. All images have descriptive alt text!
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['', 'Image', 'Product', 'Suggested Alt Text']}
                  rows={altText.map((item) => [
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                      key={`checkbox-${item.id}`}
                    />,
                    <Thumbnail
                      source={item.imageUrl || ''}
                      alt={item.currentAlt || ''}
                      size="small"
                    />,
                    item.product,
                    item.suggestedAlt,
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button size="slim" onClick={() => handleApplyFix('ALT_TEXT', item.id)}>
                        Apply
                      </Button>
                      <Button size="slim" onClick={() => handleRegenerateSuggestion('ALT_TEXT', item.id)}>
                        Regenerate
                      </Button>
                    </div>
                  ])}
                />
              )}
            </Card.Section>
          )}

          {/* Broken Links Panel */}
          {activeTab === 2 && (
            <Card.Section>
              {brokenLinks.length === 0 ? (
                <Banner status="success">
                  No broken links found. All links are working properly!
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                  headings={['', 'Broken URL', 'Source Page', 'Status', 'Suggested Fix']}
                  rows={brokenLinks.map((item) => [
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                      key={`checkbox-${item.id}`}
                    />,
                    item.url,
                    <a href={item.sourcePage} target="_blank" rel="noopener noreferrer">
                      {item.sourcePage}
                    </a>,
                    <Badge status="critical">{item.status}</Badge>,
                    item.suggestedFix || 'No suggestion',
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {item.suggestedFix && (
                        <Button size="slim" onClick={() => handleApplyFix('BROKEN_LINKS', item.id)}>
                          Create Redirect
                        </Button>
                      )}
                      <Button destructive size="slim">
                        Remove Link
                      </Button>
                    </div>
                  ])}
                />
              )}
            </Card.Section>
          )}

          {/* Content Panel */}
          {activeTab === 3 && (
            <Card.Section>
              {content.length === 0 ? (
                <Banner status="success">
                  No content issues found. Your content is well optimized!
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text']}
                  headings={['', 'Page', 'Issue', 'Suggestion']}
                  rows={content.map((item) => [
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                      key={`checkbox-${item.id}`}
                    />,
                    <a href={item.pageUrl} target="_blank" rel="noopener noreferrer">
                      {item.pageUrl}
                    </a>,
                    item.issue,
                    item.suggestion,
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button size="slim" onClick={() => handleApplyFix('CONTENT', item.id)}>
                        Apply
                      </Button>
                      <Button size="slim">
                        Edit Manually
                      </Button>
                    </div>
                  ])}
                />
              )}
            </Card.Section>
          )}
        </Tabs>
      </Card>

      {/* Bulk Actions Footer */}
      {selectedItems.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '0',
          left: '0',
          right: '0',
          backgroundColor: '#fff',
          padding: '16px',
          borderTop: '1px solid #dfe3e8',
          boxShadow: '0 -2px 4px rgba(0,0,0,0.05)',
          zIndex: '100'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <Text variant="headingMd" as="h6" fontWeight="bold">
              {selectedItems.length} items selected
            </Text>
            <div style={{ display: 'flex', gap: '16px' }}>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #c4cdd5',
                  borderRadius: '3px',
                  minWidth: '200px'
                }}
              >
                <option value="">Select bulk action</option>
                <option value="apply">Apply fixes</option>
                <option value="ignore">Ignore issues</option>
                {activeTab === 2 && <option value="redirect">Create redirects</option>}
              </select>
              <Button 
                primary 
                onClick={handleBulkApply}
                disabled={!bulkAction}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <Toast 
          content={toastContent} 
          onDismiss={() => setShowToast(false)}
          action={undoAction ? {
            content: 'Undo',
            onAction: handleUndo,
          } : undefined}
        />
      )}
    </Page>
  );
};